<script setup lang="ts">
import { LogOut, Settings } from 'lucide-vue-next';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/stores/auth.store';
import { useSidebar } from '@composables/useSidebar';

const { t } = useI18n();
const router = useRouter();
const { isCollapsed } = useSidebar();
const authStore = useAuthStore();

const userName = computed(() => authStore.user?.fullName ?? '');

const roleLabel = computed(() => {
  const role = authStore.user?.role;

  switch (role) {
    case 'admin': {
      return t('auth.roles.admin');
    }

    case 'supervisor': {
      return t('auth.roles.supervisor');
    }

    case 'technical_department': {
      return t('auth.roles.technicalDepartment');
    }

    default: {
      return authStore.user?.username ?? '';
    }
  }
});

const initials = computed(() => {
  const name = userName.value;

  return name.charAt(0).toUpperCase();
});

function handleLogout(): void {
  void authStore.logout();
}
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <button
        type="button"
        class="hover:bg-accent flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 transition-colors"
        :class="isCollapsed ? 'justify-center' : ''"
      >
        <div class="bg-muted flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full">
          <span class="text-muted-foreground text-sm font-medium">{{ initials }}</span>
        </div>

        <div v-if="!isCollapsed" class="min-w-0 text-left">
          <p class="text-foreground truncate text-sm font-medium">{{ userName }}</p>
          <p class="text-muted-foreground truncate text-xs">{{ roleLabel }}</p>
        </div>
      </button>
    </DropdownMenuTrigger>

    <DropdownMenuContent side="top" align="start" class="w-56">
      <DropdownMenuLabel class="font-normal">
        <div class="flex flex-col gap-1">
          <p class="text-sm leading-none font-medium">{{ userName }}</p>
          <p class="text-muted-foreground text-xs leading-none">{{ roleLabel }}</p>
        </div>
      </DropdownMenuLabel>

      <DropdownMenuSeparator />

      <DropdownMenuItem @select="router.push('/settings')">
        <Settings />
        {{ t('sidebar.settings') }}
      </DropdownMenuItem>

      <DropdownMenuSeparator />

      <DropdownMenuItem variant="destructive" @select="handleLogout">
        <LogOut />
        {{ t('auth.logout') }}
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
